import React from 'react';
import { Box, Button, Chip, IconButton, List, ListItem, ListItemText, Stack, Tooltip, Typography } from '@mui/material';
import ContentCutIcon from '@mui/icons-material/ContentCutOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownwardOutlined';
import type { EditList } from '../../../utils/editing/editList';
import { EDIT_LIMITS, segmentPlacements } from '../../../utils/editing/editList';
import { formatMs, keepOnly, moveSegment, removeRange, removeSegment, setSegmentSpeed, splitAt } from '../editStudioModel';
import { Choice, PanelHint } from './controls';

interface CutPanelProps {
  list: EditList;
  outputMs: number;
  selection: { fromMs: number | null; toMs: number | null };
  onSelection: (selection: { fromMs: number | null; toMs: number | null }) => void;
  selectedSegmentId: string | null;
  onSelectSegment: (id: string | null) => void;
  onChange: (next: EditList) => void;
  onSeek: (outputMs: number) => void;
}

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];

/** Trims, cuts and joins: mark a stretch and keep or remove it; cut at the playhead; speed per stretch. */
const CutPanel: React.FC<CutPanelProps> = ({ list, outputMs, selection, onSelection, selectedSegmentId, onSelectSegment, onChange, onSeek }) => {
  const placements = segmentPlacements(list);
  const hasSelection = selection.fromMs !== null && selection.toMs !== null && selection.toMs > selection.fromMs;
  const atLimit = placements.length >= EDIT_LIMITS.maxSegments;
  return (
    <Box>
      <PanelHint>Play to the moment, then mark the start and end of a stretch. Keep it (a Short), or remove it from the result.</PanelHint>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Button size="small" variant="outlined" onClick={() => onSelection({ fromMs: Math.round(outputMs), toMs: selection.toMs !== null && selection.toMs > outputMs ? selection.toMs : null })} data-testid="mark-start">Start here</Button>
        <Button size="small" variant="outlined" onClick={() => onSelection({ fromMs: selection.fromMs !== null && selection.fromMs < outputMs ? selection.fromMs : 0, toMs: Math.round(outputMs) })} data-testid="mark-end">End here</Button>
        <Tooltip title={atLimit ? `Up to ${EDIT_LIMITS.maxSegments} stretches` : 'Split the stretch at the playhead'}>
          <span>
            <Button size="small" variant="outlined" startIcon={<ContentCutIcon />} disabled={atLimit} onClick={() => onChange(splitAt(list, outputMs))} data-testid="cut-here">Cut here</Button>
          </span>
        </Tooltip>
      </Stack>
      {hasSelection ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip size="small" label={`${formatMs(selection.fromMs as number)} – ${formatMs(selection.toMs as number)}`} onDelete={() => onSelection({ fromMs: null, toMs: null })} />
          <Button size="small" variant="contained" onClick={() => { onChange(removeRange(list, selection.fromMs as number, selection.toMs as number)); onSelection({ fromMs: null, toMs: null }); onSeek(selection.fromMs as number); }} data-testid="remove-selection">Remove this stretch</Button>
          <Button size="small" variant="outlined" onClick={() => { onChange(keepOnly(list, selection.fromMs as number, selection.toMs as number)); onSelection({ fromMs: null, toMs: null }); onSeek(0); }} data-testid="keep-selection">Keep only this</Button>
        </Stack>
      ) : null}
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Kept stretches ({placements.length})</Typography>
      <List dense disablePadding data-testid="segment-list">
        {placements.map(({ segment, startMs, endMs }, index) => (
          <ListItem
            key={segment.id}
            disableGutters
            selected={segment.id === selectedSegmentId}
            onClick={() => { onSelectSegment(segment.id); onSeek(startMs); }}
            sx={{ borderRadius: 1, px: 1, cursor: 'pointer', alignItems: 'flex-start' }}
            secondaryAction={(
              <Stack direction="row" spacing={0}>
                <IconButton size="small" aria-label={`Move stretch ${index + 1} up`} disabled={index === 0} onClick={(event) => { event.stopPropagation(); onChange(moveSegment(list, segment.id, -1)); }}><ArrowUpwardIcon fontSize="inherit" /></IconButton>
                <IconButton size="small" aria-label={`Move stretch ${index + 1} down`} disabled={index === placements.length - 1} onClick={(event) => { event.stopPropagation(); onChange(moveSegment(list, segment.id, 1)); }}><ArrowDownwardIcon fontSize="inherit" /></IconButton>
                <IconButton size="small" aria-label={`Remove stretch ${index + 1}`} disabled={placements.length === 1} onClick={(event) => { event.stopPropagation(); onChange(removeSegment(list, segment.id)); }}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
              </Stack>
            )}
          >
            <ListItemText
              primary={`${index + 1}. ${formatMs(startMs)} – ${formatMs(endMs)} in the result`}
              secondary={`Source ${formatMs(segment.inMs)} – ${formatMs(segment.outMs)}${segment.speed !== 1 ? ` · ${segment.speed}×` : ''}`}
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </ListItem>
        ))}
      </List>
      {selectedSegmentId && placements.some((p) => p.segment.id === selectedSegmentId) ? (
        <Box sx={{ mt: 1.5, maxWidth: 220 }}>
          <Choice
            label="Speed of this stretch"
            value={String(placements.find((p) => p.segment.id === selectedSegmentId)?.segment.speed ?? 1)}
            options={SPEEDS.map((speed) => ({ value: String(speed), label: speed === 1 ? 'As recorded' : `${speed}×` }))}
            onChange={(value) => onChange(setSegmentSpeed(list, selectedSegmentId, Number(value)))}
            testId="segment-speed"
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default CutPanel;
